import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  SkillVm,
  SkillListItem,
  SkillListResponse,
  SkillTypeOption,
} from '@/api/types/empSkill.types'

export class EmpSkillService {
  async getSkillTypes(): Promise<SkillTypeOption[]> {
    const url = endpoints.empSkill.getSkillTypes
    const res = await apiClient.get<SkillTypeOption[] | { data: SkillTypeOption[] }>(url)
    const raw = res.data as any
    if (Array.isArray(raw)) return raw as SkillTypeOption[]
    if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data as SkillTypeOption[]
    return []
  }

  async getAll(branchId: number, pageNumber: number = 1, pageSize: number = 1000): Promise<SkillListResponse> {
    const url = endpoints.empSkill.getAll(branchId, pageNumber, pageSize)
    // Debug: see list request (Swagger GetAll)
    console.log('[EmpSkill] GET_ALL request:', { url, branchId, pageNumber, pageSize })
    const res = await apiClient.get<SkillListResponse | SkillListItem[] | { data: SkillListItem[] }>(url)
    const raw = res.data as any
    if (raw && Array.isArray(raw.data)) {
      console.log('[EmpSkill] GET_ALL response:', { totalRecords: raw.totalRecords, count: raw.data.length })
      return {
        data: raw.data,
        totalRecords: raw.totalRecords ?? raw.data.length ?? 0,
        pageNumber: 1,
        pageSize: raw.data.length,
      }
    }
    if (Array.isArray(raw)) {
      return {
        data: raw as SkillListItem[],
        totalRecords: (raw as SkillListItem[]).length,
        pageNumber: 1,
        pageSize: (raw as SkillListItem[]).length,
      }
    }
    return { data: [], totalRecords: 0, pageNumber: 1, pageSize: 0 }
  }

  async getById(id: number): Promise<SkillVm> {
    const url = endpoints.empSkill.getById(id)
    const res = await apiClient.get<SkillVm | { data: SkillVm }>(url)
    const raw = res.data as any
    if (raw && typeof raw === 'object' && 'data' in raw) return raw.data as SkillVm
    return raw as SkillVm
  }

  async getByEmpId(empId: number): Promise<SkillListItem[]> {
    const url = endpoints.empSkill.getByEmpId(empId)
    const res = await apiClient.get<SkillListItem[] | { data: SkillListItem[] }>(url)
    const raw = res.data as any
    if (Array.isArray(raw)) return raw as SkillListItem[]
    if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray(raw.data)) {
      return raw.data as SkillListItem[]
    }
    return []
  }

  async save(payload: SkillVm): Promise<unknown> {
    const url = endpoints.empSkill.save
    // Debug: see exact payload sent to backend
    console.log('[EmpSkill] SAVE payload:', { url, payload })
    const res = await apiClient.post(url, payload)
    console.log('[EmpSkill] SAVE response:', res.data)
    return res.data
  }

  async update(payload: SkillVm): Promise<unknown> {
    const url = endpoints.empSkill.update
    // Debug: see exact payload sent to backend
    console.log('[EmpSkill] UPDATE payload:', { url, payload })
    const res = await apiClient.put(url, payload)
    console.log('[EmpSkill] UPDATE response:', res.data)
    return res.data
  }

  async delete(id: number): Promise<void> {
    const url = endpoints.empSkill.delete(id)
    await apiClient.delete(url)
  }
}

export const empSkillService = new EmpSkillService()
